import React, { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { generateCreateUrlFromCategory } from '#src/services/path'
import { AnalyticsContext } from '#src/containers/Analytics'
import { HeaderProps } from '../index'
import { Logo } from '@deal/components'
import PageContent from '#src/components/PageContent'
import CategorySelectorSheet from '#src/components/CategorySelectorSheet'
import { NavLink, Link } from 'react-router-dom'
import HamburgerIcon from './hamburger.svg'
import CloseIcon from './close.svg'
import ChatSidebar from '#src/components/ChatSidebar'
import classnames from 'classnames'
import styles from './styles.css'
import useBreakpoint from '#src/hooks/useBreakpoint'

const MarketingHeader: React.FC<HeaderProps> = ({
  fixed = true,
  reserveSpace = true,
  textColor,
  transparent,
  stickyHeader,
  sidebar,
  sidebarConversation,
  fullWidth
}) => {
  const analytics = useContext(AnalyticsContext)
  const [categorySelectorExpanded, setCategorySelectorExpanded] = useState(false)
  const [navMenuExpanded, setNavMenuExpanded] = useState(false)
  const [scrolledToTop, setScrolledToTop] = useState(true)
  const history = useHistory()
  const isLargerThanLg = useBreakpoint('lg')

  textColor = textColor || (transparent ? 'light' : 'dark')

  const scrollListener = () => {
    if (Math.max(document.body.scrollTop, document.documentElement.scrollTop) === 0) {
      setScrolledToTop(true)
    } else {
      setScrolledToTop(false)
    }
  }

  useEffect(() => {
    scrollListener()
    window.addEventListener('scroll', scrollListener)
    return () => {
      window.removeEventListener('scroll', scrollListener)
    }
  })

  return (
    <div
      className={classnames(styles.wrapper, {
        [styles.reserveSpace]: reserveSpace
      })}
    >
      <header
        className={classnames(styles.header, {
          [styles.fixed]: fixed,
          [styles.transparentDark]: transparent && textColor === 'light',
          [styles.transparentLight]: transparent && textColor === 'dark',
          [styles.scrolledToTop]: scrolledToTop
        })}
      >
        {stickyHeader ? (
          <div className={styles.stickyHeader}>{stickyHeader}</div>
        ) : (
          <PageContent fullWidth={fullWidth}>
            <div className={styles.content}>
              <div className={styles.logoAndCategorySelector}>
                <Link to="/">
                  <Logo color={textColor} mark="word" className={styles.logo} />
                </Link>
                <div
                  data-testid="marketing-header-category-selector"
                  className={styles.categorySelector}
                  onClick={() => setCategorySelectorExpanded(true)}
                >
                  <div className={styles.categorySelectorLabel}>
                    {isLargerThanLg && 'Find me an expert for'}
                    {!isLargerThanLg && 'Find an expert for'}
                    <div className={styles.categorySelectorArrowDown} />
                  </div>

                  {categorySelectorExpanded && (
                    <div className={styles.categorySelectorSheetHolder}>
                      <CategorySelectorSheet
                        onRequestClose={() => setCategorySelectorExpanded(false)}
                        onCategorySelected={slug => {
                          const onboardingPath = generateCreateUrlFromCategory({
                            maybeCategorySlug: slug,
                            pathIntent: 'register',
                            sourceKey: 'marketing-header-category-sheet'
                          })

                          history.push(onboardingPath)
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div
                className={classnames(styles.navOverlay, {
                  [styles.navOverlayExpanded]: navMenuExpanded
                })}
                onClick={() => setNavMenuExpanded(false)}
              >
                <div className={styles.nav} onClick={e => e.stopPropagation()}>
                  <CloseIcon
                    className={styles.closeIcon}
                    onClick={() => setNavMenuExpanded(false)}
                  />

                  <ul className={styles.linkList}>
                    <li className={styles.linkItem}>
                      <NavLink
                        to="/how-it-works"
                        className={styles.link}
                        activeClassName={styles.headerItemActive}
                      >
                        How it works
                      </NavLink>
                    </li>
                    <li className={styles.linkItem}>
                      <NavLink
                        to="/meet-the-experts"
                        className={styles.link}
                        activeClassName={styles.headerItemActive}
                      >
                        {isLargerThanLg && 'Meet the experts'}
                        {!isLargerThanLg && 'Our experts'}
                      </NavLink>
                    </li>
                    <li className={styles.linkItem}>
                      <NavLink
                        to="/about-us"
                        className={styles.link}
                        activeClassName={styles.headerItemActive}
                      >
                        {isLargerThanLg && 'About Curated'}
                        {!isLargerThanLg && 'About us'}
                      </NavLink>
                    </li>
                    <li className={styles.linkItem}>
                      <NavLink
                        to="/reviews"
                        className={styles.link}
                        activeClassName={styles.headerItemActive}
                      >
                        Reviews
                      </NavLink>
                    </li>
                    <li className={styles.linkItem}>
                      <NavLink
                        to="/journal"
                        className={styles.link}
                        activeClassName={styles.headerItemActive}
                        onClick={() => {
                          analytics?.events.trackArticleHubEntryPointClicked({
                            entry_point: 'header'
                          })
                        }}
                      >
                        Expert Journal
                      </NavLink>
                    </li>
                    <li className={styles.linkItem}>
                      <NavLink
                        to="/auth/login"
                        rel="nofollow"
                        className={styles.link}
                        activeClassName={styles.headerItemActive}
                      >
                        Login
                      </NavLink>
                    </li>
                  </ul>

                  <Logo mark="icon" className={styles.navLogo} />
                </div>
              </div>
              <div className={styles.navIcon} onClick={() => setNavMenuExpanded(true)}>
                <HamburgerIcon />
              </div>
            </div>
          </PageContent>
        )}
      </header>

      {sidebar && (
        <ChatSidebar style={{ top: '60px', zIndex: 10000 }} conversation={sidebarConversation} />
      )}
    </div>
  )
}

export default MarketingHeader
